/**
 * @flow
 */

import moment from 'moment-timezone';

export function guessZone(date: Date): string {
  try {
    // $FlowFixMe
    return date.toLocaleString('en-US', { timeZoneName: 'short' }).match(/[\w-+]+/g).pop();
  }
  catch (err) {
    return '';
  }
}

export function stripTimezone(date: string): Date {
  const values = date.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);

  if (values instanceof Array) {
    values.shift();
    const [year, month, day, hour, minutes] = values.map(i => parseInt(i, 10));

    return new Date(year, (month - 1), day, hour, minutes);
  }

  return new Date(date);
}

// Returns a string like 'Thu, Oct 25 10:30 AM-1:30 PM EST'
export function displayDateAndTimeWithTimeZone(intl: IntlShape, start: Date, end: Date, timeZone: string): string {
  return `${displayDate(intl, start, end)} ${displayTimeWithTimeZone(intl, start, end, timeZone)}`;
}

// Returns a string like 'Thu, Oct 25 10:30 AM EST'
export function displaySingleDateTimeWithTimeZone(dateTime: Date, timezone: string): string {
  return `${moment(dateTime).format('MMM D, YYYY h:mmA')} ${moment.tz(timezone).format('z')}`;
}

export function displayDate(intl: IntlShape, start: Date, end: Date): string {
  let out = intl.formatDate(start, { weekday: 'short', month: 'short', day: 'numeric' });

  if ((start.getDate() !== end.getDate()) || (start.getMonth() !== end.getMonth())) {
    out = `${out} - ${intl.formatDate(end, { weekday: 'short', month: 'short', day: 'numeric' })}`;
  }

  return out;
}

// Returns a string like 'Oct 25'
export function displayMonthDate(intl: IntlShape, date: Date): string {
  const out = intl.formatDate(date, { month: 'short', day: 'numeric' });
  return out;
}

// Returns a string like '10:30 PM – 2:30 AM EDT (Dec 16)'
// Returns a string like '9:30 AM (Dec 16) – 10:30 AM (Dec 17) EDT'
export function displayTimeslotTimeRange(timeslot: Object): string {
  let label;
  const timeFormat = 'h:mm A';
  const dateFormat = 'MMM D';

  const startTime = moment.tz(timeslot.start_time, timeslot.tz).format(timeFormat);
  const endTime = moment.tz(timeslot.end_time, timeslot.tz).format(timeFormat);
  const timeslotStartDate = moment.tz(timeslot.start_time, timeslot.tz).format(dateFormat);
  const timeslotEndDate = moment.tz(timeslot.end_time, timeslot.tz).format(dateFormat);

  const timeZoneAbbr = moment.tz(timeslot.tz).format('z');

  if (timeslotStartDate === timeslotEndDate) {
    label = `${startTime} – ${endTime} ${timeZoneAbbr} (${timeslotStartDate})`;
  }
  else {
    label = `${startTime} (${timeslotStartDate}) – ${endTime} (${timeslotEndDate}) ${timeZoneAbbr}`;
  }

  return label;
}

// Returns a string like 'Jun 30, 10:30 PM – Jul 1, 2:30 AM'
// Returns a string like 'Jun 30, 9:30 AM–10:30 AM'
export function displayTicketTimeWithoutTimezone(intl: IntlShape, start: Date, end: Date): string {
  let out = '';

  const startTime = `${displayMonthDate(intl, start)}, ${displayTimeslotTime(intl, start)}`;
  const endTime = `${displayTimeslotTime(intl, end)}`;

  if ((start.getDate() !== end.getDate()) || (start.getMonth() !== end.getMonth())) {
    out = `${startTime} – ${displayMonthDate(intl, end)}, ${endTime}`;
  }
  else {
    out = `${startTime} – ${endTime}`;
  }

  return out;
}

// Returns a string like 'Jun 30, 10:30 PM – Jul 1, 2:30 AM EDT'
// Returns a string like 'Jun 30, 9:30 AM–10:30 AM EDT'
export function displayTicketTime(intl: IntlShape, start: Date, end: Date, timezone: string): string {
  const timeZoneAbbr = moment.tz(timezone).format('z');

  return `${displayTicketTimeWithoutTimezone(intl, start, end)} ${timeZoneAbbr}`;
}

// Returns a string like '10:30 PM'
export function displayTimeslotTime(intl: IntlShape, time: Date): string {
  return intl.formatTime(time);
}

// Returns a string like '10:30 AM-1:30 PM EST'
export function displayTimeWithTimeZone(intl: IntlShape, start: Date, end: Date, timeZone: string): string {
  // Takes the time zone location (like "America/New_York") and converts it to an abbreviated time zone ("EST")
  const timeZoneAbbr = moment.tz(timeZone).format('z');
  return `${displayTime(intl, start, end)} ${timeZoneAbbr}`;
}

export function displayTime(intl: IntlShape, start: Date, end: Date): string {
  return `${intl.formatTime(start)} – ${intl.formatTime(end)}`;
}

export function displayStartTime(intl: IntlShape, start: Date): string {
  return `${intl.formatTime(start)}`;
}

// Returns string like 'Mar 08, 2018' or 'Mar 08, 2018 + X more'
export function dateWithMoreCount(intl: IntlShape, timeSlotNodes: Object) {
  const date = stripTimezone(timeSlotNodes.slice(0, 1)[0].startAt);
  const formatted = intl.formatDate(date, { month: 'long', day: '2-digit', year: 'numeric' });

  if (timeSlotNodes.length > 1) {
    return `${formatted} + ${timeSlotNodes.length - 1} more`;
  }

  return formatted;
}

// Returns 12PM UTC in the users timezone for Auto-Withdrawal Requests
export const getLocalizedRecurringAutoRequestTime = (intl: IntlShape) => {
  const utcDate = new Date(Date.UTC(1970, 0, 7, 12)); // Generic Wednesday (Jan 7, 1970)

  // Get the localized weekday and time
  return intl.formatDate(utcDate, {
    weekday: 'short',  // Localized day name
    hour: 'numeric',  // Hour in user's timezone
    minute: 'numeric', // Optional, if you need minute precision
  });
};



// WEBPACK FOOTER //
// ./src/lib/display-date.js